import { useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import { AddItemModal } from "~/features/items/AddItemModal";
import { categoryLabel, statusLabel } from "~/features/items/labels";
import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  EmptyState,
  ListRow,
  MetricCard,
  ProtectedScreen,
  SearchableSelect,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import { usePlan } from "~/hooks/usePlan";
import { useItems } from "~/state/items";
import { useKits } from "~/state/kits";
import { spacing } from "~/styles/theme";

export default function AllItemsScreen() {
  const router = useRouter();
  const plan = usePlan();
  const kits = useKits();
  const items = useItems();
  const [term, setTerm] = useState("");
  const [kitId, setKitId] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const kitOptions = useMemo(
    () => [
      { value: "", label: "All kits" },
      { value: "ungrouped", label: "Ungrouped" },
      ...kits.map((kit) => ({ value: kit.id, label: kit.name })),
    ],
    [kits],
  );

  const kitNames = useMemo(
    () => new Map(kits.map((kit) => [kit.id, kit.name])),
    [kits],
  );

  const filtered = items.filter((item) => {
    if (kitId === "ungrouped" && item.kit_id) return false;
    if (kitId && kitId !== "ungrouped" && item.kit_id !== kitId) return false;
    const query = term.trim().toLowerCase();
    if (!query) return true;
    return [
      item.name,
      item.brand,
      item.model,
      item.serial_number,
      item.notes,
      item.vendor,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(query);
  });

  const loaned = items.filter((item) => item.status === "loaned").length;

  return (
    <ProtectedScreen
      title="All items"
      subtitle="Every tool and device you've catalogued, across every kit."
    >
      <View style={stylesheet.metrics}>
        <View style={stylesheet.metric}>
          <MetricCard
            label="Items"
            value={
              plan.items.limit === null
                ? String(items.length)
                : `${items.length} / ${plan.items.limit}`
            }
          />
        </View>
        <View style={stylesheet.metric}>
          <MetricCard label="Kits" value={String(kits.length)} />
        </View>
        <View style={stylesheet.metric}>
          <MetricCard label="Loaned out" value={String(loaned)} />
        </View>
      </View>

      <Card gap="md">
        <SectionHeader title="Find gear" />
        <View style={stylesheet.filters}>
          <View style={stylesheet.filter}>
            <AppText size="xs" weight="semibold">
              Search
            </AppText>
            <TextField
              accessibilityLabel="Search your items"
              value={term}
              placeholder="Makita, serial, van kit..."
              onChangeText={setTerm}
            />
          </View>
          <View style={stylesheet.filter}>
            <AppText size="xs" weight="semibold">
              Kit
            </AppText>
            <SearchableSelect
              title="Filter by kit"
              accessibilityLabel="Kit"
              value={kitId}
              options={kitOptions}
              placeholder="All kits"
              onValueChange={setKitId}
            />
          </View>
        </View>

        {filtered.length === 0 ? (
          <EmptyState
            title={items.length === 0 ? "No items yet" : "No matches"}
            description={
              items.length === 0
                ? "Add your first tool or device to start the catalogue."
                : "Try a different search or clear the kit filter."
            }
          />
        ) : (
          <Stack gap="none">
            {filtered.map((item, index) => (
              <ListRow
                key={item.id}
                first={index === 0}
                title={item.name}
                description={[item.brand, item.model, item.serial_number]
                  .filter(Boolean)
                  .join(" · ")}
                onPress={() => router.push(`/items/${item.id}` as never)}
                meta={
                  <>
                    <Badge tone="neutral">
                      {item.kit_id
                        ? (kitNames.get(item.kit_id) ?? "Kit")
                        : "Ungrouped"}
                    </Badge>
                    <Badge tone="neutral">{categoryLabel(item.category)}</Badge>
                    <Badge tone="neutral">{statusLabel(item.status)}</Badge>
                  </>
                }
              />
            ))}
          </Stack>
        )}

        {plan.items.allowed ? (
          <Button
            align="start"
            iconName="plus"
            onPress={() => setAddOpen(true)}
          >
            <ButtonText>Add item</ButtonText>
          </Button>
        ) : (
          <AppText size="xs">{plan.items.message}</AppText>
        )}
      </Card>

      <AddItemModal
        visible={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(itemId) => {
          setAddOpen(false);
          router.push(`/items/${itemId}` as never);
        }}
      />
    </ProtectedScreen>
  );
}

const stylesheet = StyleSheet.create({
  metrics: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
  metric: { minWidth: 180, flex: 1 },
  filters: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
  filter: { minWidth: 220, flex: 1, gap: spacing.xs },
});
